import { Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ReviewerService, ActionPlanFilters } from '../core/services/reviewer.service';
import { UserRoleService } from '../core/services/user-role.service';
import { DateFormatService } from '../core/services/date-format.service';
import { ExcelExportService } from '../core/services/excel-export.service';

interface ColumnItem {
  key: string;
  label: string;
  visible: boolean;
  width: number;
}

interface ColumnFilter {
  op1: string;
  val1: string;
}

@Component({
  standalone: true,
  selector: 'app-action-plan',
  imports: [MatIconModule, CommonModule, FormsModule],
  templateUrl: './action-plan.component.html',
  styleUrl: './action-plan.component.css',
})
export class ActionPlanComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dateFormatSvc = inject(DateFormatService);
  private excelExportSvc = inject(ExcelExportService);
  readonly pageTitle = 'Action Plan';

  isExporting = false;
  successToastMessage: string | null = null;
  hasDashboardFilter = false;

  pageSize = 10;
  currentPage = 1;
  goToPageInput = 1;
  sortColumn = 'actionPlanId';
  sortDirection: 'asc' | 'desc' = 'desc';
  isFilterOpen = false;
  isLoading = false;
  searchText = '';

  activeFilterColumn: string | null = null;
  operators = ['Contains', 'Not Contains', 'Equals', 'Starts With', 'Ends With'];

  columnSearchText = '';
  data: any[] = [];
  private defaultOrder: any[] = [];

  basicExpanded = false;
  scheduleExpanded = false;
  reviewCycleExpanded = false;
  actionPlanExpanded = false;

  filterableColumns: string[] = [
    'reviewName', 'currentStatus', 'proposedYear', 'masterClientName', 'dntProjectName',
    'contractNumber', 'additionalContractNumber', 'marketUnit', 'customerRelationshipName',
    'planName', 'qadRecommendation', 'clientQualityCQI', 'clientAccountLead', 'masterClientID',
    'deliveryCountry', 'locationsToBeReviewed', 'isPrimaryLocation', 'quarter', 'proposedMonth',
    'rcManager', 'rcSupervisor', 'rcReviewer', 'secondaryManager', 'secondarySupervisor', 'secondaryReviewer',
    'accentureOperationsSL', 'targetForReportPublications',
    'actualPublicationDate', 'overallReportingRating',
    'actionPlanId', 'issueDescription', 'actionPlanDescription', 'closedDate', 'internalDependency', 'targetDate',
    'actionPlanStatus', 'revisedTargetDate', 'linkedIssue', 'apRcSupervisor', 'apRcManager',
    'subscribersNotify', 'rcComments', 'actionRequiredBy', 'reviewObjective', 'recommendation',
    'broadIssueCategory', 'detailedIssueCategory', 'repLetterConflict',
    'repLetterQuestionsWithConflict', 'serviceLine',
  ];

  filters: { [key: string]: ColumnFilter } = {};
  private defaultVisibleKeys!: Set<string>;
  private readonly columnVisibilityStateVersion = 1;

  reviewBasicColumns: ColumnItem[] = [
    { key: 'reviewName', label: 'Review Name', visible: false, width: 12 },
    { key: 'currentStatus', label: 'Current Status', visible: false, width: 13 },
    { key: 'proposedYear', label: 'Proposed Year', visible: false, width: 13 },
    { key: 'masterClientName', label: 'Master Client Name', visible: false, width: 18 },
    { key: 'dntProjectName', label: 'D&T Project Name', visible: false, width: 17 },
    { key: 'contractNumber', label: 'Contract Number', visible: false, width: 15 },
    { key: 'additionalContractNumber', label: 'Additional Contract Number', visible: false, width: 25 },
    { key: 'marketUnit', label: 'Market Unit', visible: false, width: 11 },
    { key: 'customerRelationshipName', label: 'Customer Relationship Name', visible: false, width: 25 },
    { key: 'planName', label: 'Plan Name', visible: false, width: 10 },
    { key: 'qadRecommendation', label: 'QAD Recommendation (Opp) / QAD Overall Status (Del)', visible: false, width: 47 },
    { key: 'clientQualityCQI', label: 'Client Quality (CQI)', visible: false, width: 17 },
    { key: 'clientAccountLead', label: 'Client Account Lead (GR)', visible: false, width: 21 },
    { key: 'masterClientID', label: 'Master Client ID', visible: false, width: 14 },
    { key: 'deliveryCountry', label: 'Delivery Country*', visible: false, width: 16 },
    { key: 'locationsToBeReviewed', label: 'Locations to be reviewed*', visible: false, width: 23 },
    { key: 'isPrimaryLocation', label: 'Is it Primary Location (TBD)?', visible: false, width: 25 },
    { key: 'quarter', label: 'Quarter', visible: false, width: 8 },
    { key: 'proposedMonth', label: 'Proposed Month', visible: false, width: 15 },
    { key: 'rcManager', label: 'R&C Manager', visible: false, width: 13 },
    { key: 'rcSupervisor', label: 'R&C Supervisor', visible: false, width: 14 },
    { key: 'rcReviewer', label: 'R&C Reviewer', visible: false, width: 13 },
    { key: 'secondaryManager', label: 'Secondary Manager', visible: false, width: 17 },
    { key: 'secondarySupervisor', label: 'Secondary Supervisor', visible: false, width: 19 },
    { key: 'secondaryReviewer', label: 'Secondary Reviewer', visible: false, width: 18 },
  ];

  scheduleColumns: ColumnItem[] = [
    { key: 'accentureOperationsSL', label: 'Accenture Operation Service Line', visible: false, width: 21 },
    { key: 'targetForReportPublications', label: 'Target for Report Publications', visible: false, width: 26 },
  ];

  reviewCycleColumns: ColumnItem[] = [
    { key: 'actualPublicationDate', label: 'Actual Publication Date', visible: false, width: 20 },
    { key: 'overallReportingRating', label: 'Overall Reporting Rating', visible: false, width: 21 },
  ];

  actionPlanColumns: ColumnItem[] = [
    { key: 'actionPlanId', label: 'Action Plan ID', visible: true, width: 12 },
    { key: 'actionPlanDescription', label: 'Action Plan Description', visible: true, width: 20 },
    { key: 'closedDate', label: 'Closed Date for Action Plan', visible: true, width: 24 },
    { key: 'internalDependency', label: 'Internal Dependency', visible: true, width: 18 },
    { key: 'targetDate', label: 'Target Date', visible: true, width: 41 },
    { key: 'actionPlanStatus', label: 'Action Plan Status', visible: true, width: 16 },
    { key: 'revisedTargetDate', label: 'Revised Target Date', visible: true, width: 17 },
    { key: 'linkedIssue', label: 'Linked Issues', visible: true, width: 12 },
    { key: 'issueDescription', label: 'Issue Description', visible: true, width: 15 },
    { key: 'apRcSupervisor', label: 'R&C Supervisor', visible: false, width: 14 },
    { key: 'apRcManager', label: 'R&C Manager', visible: false, width: 13 },
    { key: 'subscribersNotify', label: 'Subscribers/Notify', visible: false, width: 16 },
    { key: 'rcComments', label: 'R&C Comments', visible: false, width: 14 },
    { key: 'actionRequiredBy', label: 'Action Required By', visible: false, width: 18 },
    { key: 'reviewObjective', label: 'Review Objective', visible: false, width: 15 },
    { key: 'recommendation', label: 'Recommendation', visible: false, width: 16 },
    { key: 'broadIssueCategory', label: 'Broad Issue Category (L1)', visible: false, width: 22 },
    { key: 'detailedIssueCategory', label: 'Detailed Issue Category (L2)', visible: false, width: 24 },
    { key: 'repLetterConflict', label: 'RepLetter Conflict', visible: false, width: 16 },
    { key: 'repLetterQuestionsWithConflict', label: 'RepLetter Questions with Conflict', visible: false, width: 29 },
    { key: 'serviceLine', label: 'Accenture Operations Service Line', visible: false, width: 11 },
  ];

  viewActionPlan(item: any): void {
    const isGoLive = Number(item.reviewTypeId) === 2;
    const urlTree = this.router.createUrlTree(['/action-plan', item.actionPlanId], {
      queryParams: { reviewId: item.reviewId, viewOnly: 'true', fromViewIcon: '1', isGoLive: isGoLive ? '1' : '0' }
    });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  editActionPlan(item: any): void {
    const status = (item.actionPlanStatus ?? '').toLowerCase();
    const viewOnly = status === 'closed' || status === 'closed-blue not tracked';
    const isGoLive = Number(item.reviewTypeId) === 2;
    const urlTree = this.router.createUrlTree(['/action-plan', item.actionPlanId], {
      queryParams: { reviewId: item.reviewId, viewOnly: viewOnly ? 'true' : 'false', isGoLive: isGoLive ? '1' : '0' }
    });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  showDeleteModal = false;
  isDeletingActionPlan = false;
  private pendingDeleteItem: any = null;

  openDeleteModal(item: any): void {
    if (!this.isAdmin) return;
    this.pendingDeleteItem = item;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.pendingDeleteItem = null;
  }

  closeSuccessToast(): void {
    this.successToastMessage = null;
    document.body.style.overflow = '';
  }

  confirmDeleteActionPlan(): void {
    if (!this.pendingDeleteItem || this.isDeletingActionPlan) return;
    this.showDeleteModal = false;
    this.isDeletingActionPlan = true;
    const item = this.pendingDeleteItem;
    const loggedUserId = this.userRoleService.enterpriseId || (this.userRoleService.email ?? '').split('@')[0];
    this.reviewerService.deleteActionPlan(item.reviewId, item.actionPlanId, loggedUserId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.data = this.data.filter(d => d !== item);
          this.defaultOrder = this.defaultOrder.filter(d => d !== item);
          this.isDeletingActionPlan = false;
          this.pendingDeleteItem = null;
          this.successToastMessage = 'Action plan deleted successfully';
          document.body.style.overflow = 'hidden';
          setTimeout(() => { this.successToastMessage = null; document.body.style.overflow = ''; }, 5000);
        },
        error: (err) => {
          console.error('Delete action plan error:', err);
          this.isDeletingActionPlan = false;
          this.pendingDeleteItem = null;
        },
      });
  }

  get isReviewer(): boolean {
    return this.userRoleService.isReviewer;
  }

  get isAdmin(): boolean {
    return this.userRoleService.isAdmin;
  }

 get isRandCMember(): boolean {
    return this.userRoleService.isRCMember;
  }

  isAssignedToRow(item: any): boolean {
    const email = (this.userRoleService.email ?? '').toLowerCase();
    const enterpriseId = (this.userRoleService.enterpriseId ?? '').toLowerCase();
    const eid = enterpriseId || (email ? email.split('@')[0] : '');
    if (!eid) return false;
    const fields = [
      item.rcManager, item.secondaryManager,
      item.rcSupervisor, item.secondarySupervisor,
      item.rcReviewer, item.secondaryReviewer,
    ];
    return fields.some(v => {
      if (!v) return false;
      return String(v).toLowerCase().split('@')[0] === eid;
    });
  }

  constructor(private reviewerService: ReviewerService, private userRoleService: UserRoleService) {
    this.filterableColumns.forEach(c => {
      this.filters[c] = { op1: 'Contains', val1: '' };
    });
    this.defaultVisibleKeys = new Set([
      ...this.reviewBasicColumns.filter(c => c.visible).map(c => c.key),
      ...this.scheduleColumns.filter(c => c.visible).map(c => c.key),
      ...this.reviewCycleColumns.filter(c => c.visible).map(c => c.key),
      ...this.actionPlanColumns.filter(c => c.visible).map(c => c.key),
    ]);
    this.restoreFilterState();
  }

  ngOnInit() {
    const apUpdatedHandler = (e: StorageEvent) => {
      if (e.key === 'ast_ap_updated') this.clearDashboardFilter();
    };
    window.addEventListener('storage', apUpdatedHandler);
    this.destroyRef.onDestroy(() => window.removeEventListener('storage', apUpdatedHandler));

    this.isLoading = true;
    const navState = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
    let rawDashboardFilters = navState?.['actionPlanFilters'];
    if (!rawDashboardFilters) {
      const filterToken = this.route.snapshot.queryParamMap.get('dashboardFilterToken');
      const storageKey = filterToken
        ? `pending_actionPlanFilters:${filterToken}`
        : 'pending_actionPlanFilters';
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try { rawDashboardFilters = JSON.parse(stored); } catch { }
        localStorage.removeItem(storageKey);
      } else if (filterToken) {
        const legacyStored = localStorage.getItem('pending_actionPlanFilters');
        if (legacyStored) {
          try { rawDashboardFilters = JSON.parse(legacyStored); } catch { }
          localStorage.removeItem('pending_actionPlanFilters');
        }
      }
    }
    const initialFilters = this.getInitialActionPlanFilters(rawDashboardFilters);
    this.hasDashboardFilter = !!rawDashboardFilters && Object.values(rawDashboardFilters).some(v => v !== null && v !== undefined && v !== '');
    history.replaceState({}, '', window.location.href);
    //this.reviewerService.getActionPlansEAV(null, null)
    this.reviewerService.getActionPlanDetails(initialFilters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.data = (res || []).map(item => {
            const issues: any[] = Array.isArray(item.IssueFields) ? item.IssueFields : [];
            const joinField = (key: string) =>
              [...new Set(issues.map(i => String(i[key] ?? '')).filter(Boolean))].join(', ');

            return {
              reviewName: item.ReviewName ?? '',
              currentStatus: item.CurrentStatus ?? '',
              proposedYear: item.Review_ProposedYear ?? '',
              masterClientName: item.Review_ClientName ?? item.Review_MasterClientName ?? '',
              dntProjectName: item.Review_ProjectName ?? item['Review_D&TProjectName'] ?? item.Review_DTProjectName ?? '',
              contractNumber: item.Review_ContractNumber ?? '',
              additionalContractNumber: item.Review_AdditionalContractNumber ?? '',
              marketUnit: item.Review_MarketUnit ?? '',
              customerRelationshipName: item.Review_CustomerRelationshipName ?? '',
              planName: item.Review_PlanName ?? '',
              qadRecommendation: item.Review_QADRecommendation ?? '',
              clientQualityCQI: item.Review_ClientQualityCQI ?? '',
              clientAccountLead: item.Review_ClientAccountLead ?? '',
              masterClientID: item.Review_MasterClientID ?? '',
              deliveryCountry: item.Review_DeliveryCountry ?? '',
              locationsToBeReviewed: item.Review_LocationToBeReviewed ?? '',
              isPrimaryLocation: (item.Review_IsParentLocation === '1' || item.Review_IsParentLocation === 1) ? 'Yes' : 'No',
              quarter: item.Review_Quarter ?? '',
              proposedMonth: item.Review_ProposedMonth ?? '',
              rcManager: item.Review_RCManager ?? '',
              rcSupervisor: item.Review_RCSupervisor ?? '',
              rcReviewer: item.Review_RCReviewer ?? '',
              secondaryManager: item.Review_secondaryRCManager ?? item.Review_SecondaryRCManager ?? item.Review_SecondaryRcManager ?? item.Review_SecondaryManager ?? '',
              secondarySupervisor: item.Review_secondaryRCSupervisor ?? item.Review_SecondaryRCSupervisor ?? item.Review_SecondaryRcSupervisor ?? item.Review_SecondarySupervisor ?? '',
              secondaryReviewer: item.Review_secondaryRCReviewer ?? item.Review_SecondaryRCReviewer ?? item.Review_SecondaryRcReviewer ?? item.Review_SecondaryReviewer ?? '',
              accentureOperationsSL: item.Schedule_AccentureOperationsServiceLine ?? item.Schedule_ServiceLine ?? '',
              targetForReportPublications: this.toDisplayDate(item.Schedule_TargetPublicationDate ?? item.Schedule_TargetForReportPublication ?? ''),
              actualPublicationDate: this.toDisplayDate(item.ActualPublicationDate ?? ''),
              overallReportingRating: item.OverallReportingRating ?? '',
              reviewId: item.ReviewID ?? null,
              reviewTypeId: item.Review_ReviewTypeID ?? item.ReviewTypeID ?? null,
              actionPlanId: item.ActionPlanID ?? '',
              issueDescription: joinField('IssueDescription'),
              actionPlanDescription: item.ActionPlanDescription ?? item.Description ?? '',
              closedDate: this.toDisplayDate(item.ClosedDate ?? ''),
              internalDependency: Object.prototype.hasOwnProperty.call(item, 'AP_InternalDependency')
                ? (item.AP_InternalDependency ?? '')
                : (item['Internal dependency'] || item.InternalDependency || ''),
              targetDate: this.toDisplayDate(item.TargetDate ?? ''),
              actionPlanStatus: item.Status ?? item.ActionPlanStatus ?? '',
              revisedTargetDate: this.toDisplayDate(item.RevisedTargetDate ?? ''),
              linkedIssue: issues.length
                ? issues.map(i => i.IssueID != null ? `I-${i.IssueID}` : '').filter(Boolean).join(', ')
                : (item.LinkedIssueNames ?? ''),
              apRcSupervisor: item.Review_RCSupervisor ?? '',
              apRcManager: item.Review_RCManager ?? '',
              subscribersNotify: joinField('SubscriberNames') ?? '',
              rcComments: item.RCComments ?? '',
              actionRequiredBy: joinField('ActionRequiredBy'),
              reviewObjective: joinField('ReviewObjective'),
              recommendation: joinField('Recommendation'),
              broadIssueCategory: joinField('BroadIssueCategoryL1'),
              detailedIssueCategory: joinField('DetailedIssueCategoryL2'),
              repLetterConflict: (() => {
                const v = joinField('RepLetterConflict') ?? '';
                const lv = v.toLowerCase();
                if (lv === 'true'  || lv === 'yes') return 'Yes';
                if (lv === 'false' || lv === 'no')  return 'No';
                if (lv === 'not applicable') return 'Not Applicable';
                return v;
              })(),
              repLetterQuestionsWithConflict: joinField('RepLetter Questions with Conflict') || joinField('RepLetterQuestions'),
              serviceLine: joinField('ServiceLine') || joinField('Accenture Operation Service Line'),
            };
        });
          this.applyDefaultSort();
          this.defaultOrder = [...this.data];
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          console.error('getActionPlans error:', err);
        }
      });
  }

  clearDashboardFilter(): void {
    this.hasDashboardFilter = false;
    this.isLoading = true;
    this.reviewerService.getActionPlanDetails(this.getInitialActionPlanFilters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.data = (res || []).map(item => {
            const issues: any[] = Array.isArray(item.IssueFields) ? item.IssueFields : [];
            const joinField = (key: string) =>
              [...new Set(issues.map(i => String(i[key] ?? '')).filter(Boolean))].join(', ');
            return {
              reviewName: item.ReviewName ?? '',
              currentStatus: item.CurrentStatus ?? '',
              proposedYear: item.Review_ProposedYear ?? '',
              masterClientName: item.Review_ClientName ?? item.Review_MasterClientName ?? '',
              dntProjectName: item.Review_ProjectName ?? item['Review_D&TProjectName'] ?? item.Review_DTProjectName ?? '',
              contractNumber: item.Review_ContractNumber ?? '',
              additionalContractNumber: item.Review_AdditionalContractNumber ?? '',
              marketUnit: item.Review_MarketUnit ?? '',
              customerRelationshipName: item.Review_CustomerRelationshipName ?? '',
              planName: item.Review_PlanName ?? '',
              qadRecommendation: item.Review_QADRecommendation ?? '',
              clientQualityCQI: item.Review_ClientQualityCQI ?? '',
              clientAccountLead: item.Review_ClientAccountLead ?? '',
              masterClientID: item.Review_MasterClientID ?? '',
              deliveryCountry: item.Review_DeliveryCountry ?? '',
              locationsToBeReviewed: item.Review_LocationToBeReviewed ?? '',
              isPrimaryLocation: (item.Review_IsParentLocation === '1' || item.Review_IsParentLocation === 1) ? 'Yes' : 'No',
              quarter: item.Review_Quarter ?? '',
              proposedMonth: item.Review_ProposedMonth ?? '',
              rcManager: item.Review_RCManager ?? '',
              rcSupervisor: item.Review_RCSupervisor ?? '',
              rcReviewer: item.Review_RCReviewer ?? '',
              secondaryManager: item.Review_secondaryRCManager ?? item.Review_SecondaryRCManager ?? item.Review_SecondaryRcManager ?? item.Review_SecondaryManager ?? '',
              secondarySupervisor: item.Review_secondaryRCSupervisor ?? item.Review_SecondaryRCSupervisor ?? item.Review_SecondaryRcSupervisor ?? item.Review_SecondarySupervisor ?? '',
              secondaryReviewer: item.Review_secondaryRCReviewer ?? item.Review_SecondaryRCReviewer ?? item.Review_SecondaryRcReviewer ?? item.Review_SecondaryReviewer ?? '',
              accentureOperationsSL: item.Schedule_AccentureOperationsServiceLine ?? item.Schedule_ServiceLine ?? '',
              targetForReportPublications: this.toDisplayDate(item.Schedule_TargetPublicationDate ?? item.Schedule_TargetForReportPublication ?? ''),
              actualPublicationDate: this.toDisplayDate(item.ActualPublicationDate ?? ''),
              overallReportingRating: item.OverallReportingRating ?? '',
              reviewId: item.ReviewID ?? null,
              reviewTypeId: item.Review_ReviewTypeID ?? item.ReviewTypeID ?? null,
              actionPlanId: item.ActionPlanID ?? '',
              issueDescription: joinField('IssueDescription'),
              actionPlanDescription: item.ActionPlanDescription ?? item.Description ?? '',
              closedDate: this.toDisplayDate(item.ClosedDate ?? ''),
              internalDependency: Object.prototype.hasOwnProperty.call(item, 'AP_InternalDependency')
                ? (item.AP_InternalDependency ?? '')
                : (item['Internal dependency'] || item.InternalDependency || ''),
              targetDate: this.toDisplayDate(item.TargetDate ?? ''),
              actionPlanStatus: item.Status ?? item.ActionPlanStatus ?? '',
              revisedTargetDate: this.toDisplayDate(item.RevisedTargetDate ?? ''),
              linkedIssue: issues.length
                ? issues.map(i => i.IssueID != null ? `I-${i.IssueID}` : '').filter(Boolean).join(', ')
                : (item.LinkedIssueNames ?? ''),
              apRcSupervisor: item.Review_RCSupervisor ?? '',
              apRcManager: item.Review_RCManager ?? '',
              subscribersNotify: joinField('SubscriberNames') ?? '',
              rcComments: item.RCComments ?? '',
              actionRequiredBy: joinField('ActionRequiredBy'),
              reviewObjective: joinField('ReviewObjective'),
              recommendation: joinField('Recommendation'),
              broadIssueCategory: joinField('BroadIssueCategoryL1'),
              detailedIssueCategory: joinField('DetailedIssueCategoryL2'),
              repLetterConflict: (() => {
                const v = joinField('RepLetterConflict') ?? '';
                const lv = v.toLowerCase();
                if (lv === 'true'  || lv === 'yes') return 'Yes';
                if (lv === 'false' || lv === 'no')  return 'No';
                if (lv === 'not applicable') return 'Not Applicable';
                return v;
              })(),
              repLetterQuestionsWithConflict: joinField('RepLetter Questions with Conflict') || joinField('RepLetterQuestions'),
              serviceLine: joinField('ServiceLine') || joinField('Accenture Operation Service Line'),
            };
          });
          this.applyDefaultSort();
          this.defaultOrder = [...this.data];
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          console.error('clearDashboardFilter error:', err);
        },
      });
  }

  private getInitialActionPlanFilters(filters?: Partial<ActionPlanFilters>): ActionPlanFilters {
    return {
      ReviewID: filters?.ReviewID ?? null,
      PropsedYearId: filters?.PropsedYearId ?? null,
      Quarter: filters?.Quarter ?? null,
      ProposedMonth: filters?.ProposedMonth ?? null,
      ClientName: filters?.ClientName ?? null,
      DeliveryCountry: filters?.DeliveryCountry ?? null,
      LocationToBeReviewed: filters?.LocationToBeReviewed ?? null,
      TeamMembers: filters?.TeamMembers ?? null,
      Region: filters?.Region ?? null,
      IssueID: filters?.IssueID ?? null,
      ReviewTypeID: filters?.ReviewTypeID ?? null,
      ActionPlanID: filters?.ActionPlanID ?? null,
      ActionPlanStatus: filters?.ActionPlanStatus ?? null,
    };
  }

  private readonly dateSortCols = new Set([
    'closedDate', 'targetDate', 'revisedTargetDate', 'actualPublicationDate', 'targetForReportPublications'
  ]);

  private readonly monthOrder: Record<string, number> = {
    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
    Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
  };

  private parseDateSortKey(val: string): number {
    // DD-MMM-YYYY  e.g. "23-Jun-2026"
    const dmy = /^(\d{2})-([A-Za-z]{3})-(\d{4})$/.exec(val);
    if (dmy) {
      return +dmy[3] * 10000 + (this.monthOrder[dmy[2]] ?? 0) * 100 + +dmy[1];
    }
    // YYYY-MM-DD  e.g. "2026-06-23"
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(val);
    if (iso) {
      return +iso[1] * 10000 + +iso[2] * 100 + +iso[3];
    }
    return -1; // blanks / unparseable sort first
  }

  private compareForSort(va: string, vb: string, col: string): number {
    if (this.dateSortCols.has(col)) {
      return this.parseDateSortKey(va) - this.parseDateSortKey(vb);
    }
    // Text: blanks first, then digit-leading strings, then alphabetic
    if (va === '' && vb === '') return 0;
    if (va === '') return -1;
    if (vb === '') return 1;
    const aDigit = /^\d/.test(va);
    const bDigit = /^\d/.test(vb);
    if (aDigit !== bDigit) return aDigit ? -1 : 1;
    return va.localeCompare(vb, 'en', { numeric: true, sensitivity: 'base' });
  }

  private applyDefaultSort() {
    const col = this.sortColumn || 'actionPlanId';
    const dir = this.sortDirection;
    this.data.sort((a, b) => {
      const va = String(a[col] ?? '').trim();
      const vb = String(b[col] ?? '').trim();
      const cmp = this.compareForSort(va, vb, col);
      return dir === 'asc' ? cmp : -cmp;
    });
  }

  private toDisplayDate(value: string): string {
    return this.dateFormatSvc.format(value);
  }

  /** Every exportable data column, in grid display order. Action columns are never part of this list. */
  private exportableColumns(): ColumnItem[] {
    return [
      ...this.actionPlanColumns,
      ...this.reviewBasicColumns,
      ...this.scheduleColumns,
      ...this.reviewCycleColumns,
    ];
  }

  isColumnVisible(key: string): boolean {
    const col = this.exportableColumns().find(c => c.key === key);
    return col ? col.visible : false;
  }

  visibleColumnCount(): number {
    return this.exportableColumns().filter(c => c.visible).length;
  }

  totalColumnCount(): number {
    return this.exportableColumns().length;
  }

  private exportData(): any[] {
    if (!this.defaultOrder.length) return this.data;
    const currentRows = new Set(this.data);
    const savedRows = new Set(this.defaultOrder);
    return [
      ...this.defaultOrder.filter(row => currentRows.has(row)),
      ...this.data.filter(row => !savedRows.has(row)),
    ];
  }

  async exportAll(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      const columns = this.exportableColumns().map(c => ({ key: c.key, header: c.label, width: c.width }));
      const fileName = this.excelExportSvc.buildFileName('ActionPlan_ExportAll');
      await this.excelExportSvc.exportToExcel(columns, this.exportData(), fileName, 'Action Plan Export All');
    } catch (err) {
      console.error('Export All failed:', err);
    } finally {
      this.isExporting = false;
    }
  }

  async exportVisible(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      const columns = this.exportableColumns()
        .filter(c => c.visible)
        .map(c => ({ key: c.key, header: c.label, width: c.width }));
      const fileName = this.excelExportSvc.buildFileName('ActionPlan_ExportVisible');
      await this.excelExportSvc.exportToExcel(columns, this.exportData(), fileName, 'Action Plan Export Visible');
    } catch (err) {
      console.error('Export Visible failed:', err);
    } finally {
      this.isExporting = false;
    }
  }

  filteredBasicColumns(): ColumnItem[] {
    return this.applySearch(this.reviewBasicColumns);
  }

  filteredScheduleColumns(): ColumnItem[] {
    return this.applySearch(this.scheduleColumns);
  }

  filteredReviewCycleColumns(): ColumnItem[] {
    return this.applySearch(this.reviewCycleColumns);
  }

  filteredActionPlanColumns(): ColumnItem[] {
    return this.applySearch(this.actionPlanColumns);
  }

  private applySearch(cols: ColumnItem[]): ColumnItem[] {
    const q = (this.columnSearchText || '').toLowerCase().trim();
    return q ? cols.filter(c => c.label.toLowerCase().includes(q)) : cols;
  }

  isBasicExpanded(): boolean {
    const hasSearch = !!this.columnSearchText.trim();
    if (hasSearch) return this.filteredBasicColumns().length > 0;
    return this.basicExpanded;
  }

  isScheduleExpanded(): boolean {
    const hasSearch = !!this.columnSearchText.trim();
    if (hasSearch) return this.filteredScheduleColumns().length > 0;
    return this.scheduleExpanded;
  }

  isReviewCycleExpanded(): boolean {
    const hasSearch = !!this.columnSearchText.trim();
    if (hasSearch) return this.filteredReviewCycleColumns().length > 0;
    return this.reviewCycleExpanded;
  }

  isActionPlanExpanded(): boolean {
    const hasSearch = !!this.columnSearchText.trim();
    if (hasSearch) return this.filteredActionPlanColumns().length > 0;
    return this.actionPlanExpanded;
  }

  toggleFilterPanel() {
    this.isFilterOpen = !this.isFilterOpen;
  }

  toggleColumnFilter(col: string, event: Event) {
    event.stopPropagation();
    this.activeFilterColumn = this.activeFilterColumn === col ? null : col;
  }

  applyFilter() {
    this.activeFilterColumn = null;
    this.currentPage = 1;
    this.saveFilterState();
  }

  resetFilter(col: string) {
    this.filters[col] = { op1: 'Contains', val1: '' };
    this.currentPage = 1;
    this.saveFilterState();
  }

  hasActiveFilter(col: string): boolean {
    const f = this.filters[col];
    return !!(f && f.val1);
  }

  private matchOperator(cell: string, op: string, val: string): boolean {
    if (!val) return true;
    const c = cell.toLowerCase();
    const v = val.toLowerCase();
    switch (op) {
      case 'Contains':     return c.includes(v);
      case 'Not Contains': return !c.includes(v);
      case 'Equals':       return c === v;
      case 'Starts With':  return c.startsWith(v);
      case 'Ends With':    return c.endsWith(v);
      default:             return true;
    }
  }

  filteredData(): any[] {
    const term = this.searchText.trim().toLowerCase();
    return this.data.filter(row => {
      if (term) {
        const visibleKeys = this.exportableColumns().filter(c => c.visible).map(c => c.key);
        const matches = visibleKeys.some(col =>
          String((row as any)[col] ?? '').toLowerCase().includes(term)
        );
        if (!matches) return false;
      }
      return this.filterableColumns.every(col => {
        const f = this.filters[col];
        if (!f || !f.val1) return true;
        return this.matchOperator(String((row as any)[col] ?? ''), f.op1, f.val1);
      });
    });
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredData().length / this.pageSize));
  }

  totalPagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 10;
    const startPage = Math.floor((this.currentPage - 1) / maxVisible) * maxVisible + 1;
    const endPage = Math.min(startPage + maxVisible - 1, total);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  goToPage() {
    if (this.goToPageInput >= 1 && this.goToPageInput <= this.totalPages()) {
      this.currentPage = this.goToPageInput;
    }
  }

  firstPage() {
    this.currentPage = 1;
    this.goToPageInput = 1;
  }

  prevPage() {
    if (this.currentPage > 1) this.currentPage--;
  }

  nextPage() {
    if (this.currentPage < this.totalPages()) this.currentPage++;
  }

  lastPage() {
    this.currentPage = this.totalPages();
    this.goToPageInput = this.currentPage;
  }

  sort(column: string) {
    this.activeFilterColumn = null;
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyDefaultSort();
    this.currentPage = 1;
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'unfold_more';
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: any) {
    const clickedInsideTable = event.target.closest('table');
    const clickedInsideFilter = event.target.closest('.filter-dropdown');
    if (!clickedInsideTable && !clickedInsideFilter) {
      this.activeFilterColumn = null;
    }
  }

  setTooltipIfTruncated(event: MouseEvent, text: string): void {
    const td = event.currentTarget as HTMLElement;
    const span = td.querySelector('.truncate-text') as HTMLElement;
    if (span && span.scrollWidth > span.clientWidth) {
      td.setAttribute('data-tip', text ?? '');
    } else {
      td.removeAttribute('data-tip');
    }
  }

  selectAll(): void {
    [...this.reviewBasicColumns, ...this.scheduleColumns, ...this.reviewCycleColumns, ...this.actionPlanColumns]
      .forEach(c => c.visible = true);
    this.saveFilterState();
  }

  clearAll(): void {
    [...this.reviewBasicColumns, ...this.scheduleColumns, ...this.reviewCycleColumns, ...this.actionPlanColumns]
      .forEach(c => {
        c.visible = this.defaultVisibleKeys.has(c.key);
      });
    this.saveFilterState();
  }

  saveFilterState(): void {
    const state = {
      columnVisibilityStateVersion: this.columnVisibilityStateVersion,
      filters: this.filters,
      searchText: this.searchText,
      columnVisibility: {
        reviewBasicColumns: this.reviewBasicColumns.map(c => ({ key: c.key, visible: c.visible })),
        scheduleColumns: this.scheduleColumns.map(c => ({ key: c.key, visible: c.visible })),
        reviewCycleColumns: this.reviewCycleColumns.map(c => ({ key: c.key, visible: c.visible })),
        actionPlanColumns: this.actionPlanColumns.map(c => ({ key: c.key, visible: c.visible })),
      },
    };
    sessionStorage.setItem('ap_filter_state', JSON.stringify(state));
  }

  private restoreFilterState(): void {
    const raw = sessionStorage.getItem('ap_filter_state');
    if (!raw) return;
    try {
      const state = JSON.parse(raw);
      if (state.filters) {
        this.filterableColumns.forEach(col => {
          if (state.filters[col]) this.filters[col] = state.filters[col];
        });
      }
      if (typeof state.searchText === 'string') this.searchText = state.searchText;
      if (
        state.columnVisibilityStateVersion === this.columnVisibilityStateVersion &&
        state.columnVisibility
      ) {
        this.applyColumnVisibility(this.reviewBasicColumns, state.columnVisibility.reviewBasicColumns);
        this.applyColumnVisibility(this.scheduleColumns, state.columnVisibility.scheduleColumns);
        this.applyColumnVisibility(this.reviewCycleColumns, state.columnVisibility.reviewCycleColumns);
        this.applyColumnVisibility(this.actionPlanColumns, state.columnVisibility.actionPlanColumns);
      }
    } catch {
      sessionStorage.removeItem('ap_filter_state');
    }
  }

  private applyColumnVisibility(cols: ColumnItem[], saved: { key: string; visible: boolean }[] | undefined): void {
    if (!Array.isArray(saved)) return;
    const map = new Map(saved.map((s: { key: string; visible: boolean }) => [s.key, s.visible]));
    cols.forEach(c => { if (map.has(c.key)) c.visible = map.get(c.key)!; });
  }

  displayActionPlanId(id: any): string {
    if (id === null || id === undefined || id === '') return '';
    const s = String(id).trim();
    return /^ap-/i.test(s) ? s : `AP-${s}`;
  }
}
